import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import {
  Eyebrow,
  HeroPhoto,
  HeroWash,
  Rule,
  Wordmark,
} from "@/components/editorial";
import { PHOTOS } from "@/lib/photos";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/AuthProvider";
import {
  organizationSchema,
  softwareAppSchema,
  faqSchema,
} from "@/lib/seo/schemas";

/**
 * Editorial landing page — see /tmp/design (Kinfolk direction).
 * Layout: Nav · Hero · Meta strip · Chapter 1 ritual · Chapter 2 menu ·
 *         Chapter 3 IA · Chapter 4 pricing · Final CTA · Footer.
 */
export function LandingPage() {
  return (
    <div className="min-h-full bg-background font-sans text-foreground">
      <Seo
        canonicalPath="/"
        jsonLd={[organizationSchema, softwareAppSchema, faqSchema]}
      />
      <NavBar />
      <Hero />
      <MetaStrip />
      <ChapterRitual />
      <ChapterMenu />
      <ChapterIntelligence />
      <ChapterPricing />
      <FinalCTA />
      <SiteFooter />
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────
function NavBar() {
  const { session } = useAuth();
  return (
    <header className="sticky top-0 z-10 flex items-center justify-between border-b border-hair bg-background/95 px-5 py-5 backdrop-blur lg:px-[72px]">
      <Wordmark size="md" />
      <nav className="hidden gap-9 text-[13px] text-ink-70 md:flex">
        <a href="#concept" className="hover:text-foreground">
          Le concept
        </a>
        <a href="#menu" className="hover:text-foreground">
          Cuisine de la semaine
        </a>
        <a href="#tarifs" className="hover:text-foreground">
          Tarifs
        </a>
        <Link to="/faq" className="hover:text-foreground">
          FAQ
        </Link>
      </nav>
      <div className="flex items-center gap-3 text-[13px]">
        {session ? (
          <Button asChild size="sm">
            <Link to="/menu">
              Mon menu <ArrowRight />
            </Link>
          </Button>
        ) : (
          <>
            <Link
              to="/login"
              className="hidden text-ink-70 hover:text-foreground sm:inline"
            >
              Se connecter
            </Link>
            <Button asChild size="sm">
              <Link to="/onboarding">
                Commencer <ArrowRight />
              </Link>
            </Button>
          </>
        )}
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative overflow-hidden px-5 pb-24 pt-20 lg:px-[72px] lg:pb-24 lg:pt-28">
      <HeroPhoto src={PHOTOS.bowls} blur={14} scale={1.04} />
      <HeroWash intensity="default" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, oklch(var(--background) / 0.45) 0%, oklch(var(--background) / 0) 55%)",
        }}
      />

      <div className="relative max-w-[1100px]">
        <Eyebrow rule className="mb-8">
          Numéro 24 · Printemps 2026
        </Eyebrow>
        <h1 className="h-display mb-8 text-display-xl text-foreground">
          Une après-midi.
          <br />
          <span className="italic text-accent">Sept</span> dîners
          <span className="text-ink-50">.</span>
        </h1>
        <p className="mb-12 max-w-[640px] font-serif text-xl font-light leading-snug text-ink-70 lg:text-[24px]">
          L'application qui orchestre votre <em>batch cooking</em> hebdomadaire
          — de vos contraintes nutritionnelles jusqu'à la liste de courses,
          rangée allée par allée.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <Button asChild size="lg">
            <Link to="/onboarding">
              Composer mon menu <ArrowRight />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/menu">Voir un menu type</Link>
          </Button>
          <span className="ml-1 text-[12.5px] text-ink-50">
            Essai 14 jours · sans carte
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── Meta strip ───────────────────────────────────────────────────────
const META = [
  ["2 h 30", "temps de cuisine"],
  ["7 dîners", "par session"],
  ["—38 %", "gaspillage"],
  ["—54 €", "budget hebdo*"],
] as const;

function MetaStrip() {
  return (
    <div className="mx-5 grid grid-cols-2 border-y border-hair sm:grid-cols-4 lg:mx-[72px]">
      {META.map(([big, small], i) => (
        <div
          key={small}
          className={cn(
            "px-5 py-6 sm:px-7 sm:py-7",
            i > 0 && "border-l border-hair",
            // 2-column mobile: lift the left-border of the 2nd cell only
            i === 2 && "sm:border-l",
            i === 2 && "border-l-0 sm:border-l",
          )}
        >
          <div className="font-serif text-[34px] font-normal tracking-[-0.01em] text-foreground">
            {big}
          </div>
          <div className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-ink-50">
            {small}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Chapter 01 — Le rituel du dimanche ───────────────────────────────
function ChapterRitual() {
  const steps = [
    {
      n: "01",
      t: "Vos préférences,\nune fois pour toutes.",
      b: "Allergies, régime, équipement, temps disponible, taille du foyer. Quelques questions, puis nous n'en reparlons plus.",
    },
    {
      n: "02",
      t: "L'IA compose\nvotre menu.",
      b: "Sept dîners équilibrés, optimisés pour les bases communes — un seul rôti, trois recettes. Vous validez, ajustez, remplacez.",
    },
    {
      n: "03",
      t: "Liste de courses,\nrangée par rangée.",
      b: "Triée par allée du marché ou du supermarché, avec budget estimé. Imprimable, partageable, intelligemment groupée.",
    },
  ];
  return (
    <section
      id="concept"
      className="bg-background px-5 py-24 lg:px-[72px] lg:py-[120px]"
    >
      <div className="mb-16 grid gap-12 lg:mb-20 lg:grid-cols-[320px_1fr] lg:gap-24">
        <div>
          <Eyebrow className="mb-4">Chapitre 01</Eyebrow>
          <h2 className="h-display text-display-md">
            Le <span className="italic">rituel</span> du
            <br />
            dimanche.
          </h2>
        </div>
        <div className="max-w-[720px] pt-2 font-serif text-xl font-light leading-relaxed text-ink-70 lg:text-[22px]">
          Cuisiner une fois, dîner sept fois. Le batch cooking n'est pas une
          mode — c'est un retour à un geste simple : préparer ses bases en lot,
          puis composer ses repas au fil de la semaine. Nous l'avons rendu
          intelligent.
        </div>
      </div>

      <div className="grid gap-10 lg:grid-cols-3 lg:gap-12">
        {steps.map((s) => (
          <article key={s.n}>
            <div className="mb-4 font-serif text-lg italic text-accent">
              {s.n}
            </div>
            <Rule className="mb-5" />
            <h3 className="mb-4 whitespace-pre-line font-serif text-[28px] font-normal leading-tight tracking-[-0.005em]">
              {s.t}
            </h3>
            <p className="text-[14.5px] leading-relaxed text-ink-70">{s.b}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── Chapter 02 — Cette semaine, au menu ──────────────────────────────
function ChapterMenu() {
  const meals = [
    {
      day: "Lun",
      name: "Poulet rôti aux\nherbes du jardin",
      tag: "Base · 1",
      kcal: "520",
      time: "40 min",
      img: PHOTOS.spread,
    },
    {
      day: "Mar",
      name: "Bowl de quinoa,\nchèvre & figues",
      tag: "Méditerranéen",
      kcal: "480",
      time: "15 min",
      img: PHOTOS.bowls,
    },
    {
      day: "Mer",
      name: "Curry de\nlentilles corail",
      tag: "Végétarien",
      kcal: "510",
      time: "25 min",
      img: PHOTOS.ingredients,
    },
    {
      day: "Jeu",
      name: "Salade tiède,\npoulet & noisettes",
      tag: "Reste · J1",
      kcal: "460",
      time: "12 min",
      img: PHOTOS.greens,
    },
  ];
  return (
    <section
      id="menu"
      className="bg-paper px-5 py-24 lg:px-[72px] lg:py-[120px]"
    >
      <div className="mb-12 flex flex-wrap items-end justify-between gap-6 lg:mb-16">
        <div>
          <Eyebrow className="mb-4">Chapitre 02</Eyebrow>
          <h2 className="h-display text-display-lg">
            Cette semaine, <span className="italic">au menu</span>.
          </h2>
        </div>
        <div className="text-right text-[13px] leading-relaxed text-ink-50">
          Semaine 19
          <br />
          <span className="font-serif text-base italic text-ink-70">
            du 11 au 17 mai
          </span>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {meals.map((m) => (
          <article
            key={m.day}
            className="overflow-hidden border border-hair bg-background"
          >
            <div
              className="relative h-[220px] overflow-hidden bg-cover bg-center"
              style={{ backgroundImage: `url(${m.img})` }}
            >
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.18))",
                }}
              />
              <div className="absolute left-3.5 top-3.5 bg-background px-2.5 py-1 font-serif text-[13px] italic">
                {m.day}.
              </div>
            </div>
            <div className="px-5 pb-6 pt-5">
              <div className="mb-3 text-[10.5px] uppercase tracking-[0.18em] text-accent">
                {m.tag}
              </div>
              <h3 className="mb-4 whitespace-pre-line font-serif text-[22px] font-normal leading-tight">
                {m.name}
              </h3>
              <div className="flex justify-between border-t border-hair pt-3.5 text-xs text-ink-50">
                <span>{m.kcal} kcal</span>
                <span>{m.time}</span>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Link
          to="/menu"
          className="border-b border-foreground pb-1 text-[13px] uppercase tracking-[0.08em] text-foreground hover:text-accent hover:border-accent"
        >
          Voir les sept dîners
        </Link>
      </div>
    </section>
  );
}

// ─── Chapter 03 — Une intelligence qui cuisine ────────────────────────
function ChapterIntelligence() {
  const items = [
    [
      "Contraintes nutritionnelles",
      "Calories, macros, indice nutritionnel, allergènes — l'IA respecte vos seuils sans vous y enfermer.",
    ],
    [
      "Thèmes hebdomadaires",
      "Méditerranéen, low-carb, anti-inflammatoire, saison — vous choisissez l'angle.",
    ],
    [
      "Remplacement intelligent",
      "Un dîner ne vous tente pas ? L'IA propose une alternative qui réutilise les mêmes bases.",
    ],
    [
      "Saisonnalité & terroir",
      "Suggestions ancrées dans les produits du moment et les marchés près de chez vous.",
    ],
  ] as const;
  return (
    <section
      id="intelligence"
      className="bg-background px-5 py-24 lg:px-[72px] lg:py-[120px]"
    >
      <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
        <div className="relative h-[440px] overflow-hidden lg:h-[560px]">
          <HeroPhoto src={PHOTOS.vegMarket} blur={2} opacity={0.92} />
          <div className="absolute inset-x-6 bottom-6 border-l-2 border-accent bg-background px-6 py-5 font-serif text-[17px] italic leading-snug text-ink-70">
            « Substituer les épinards par du chou kale —
            <br />
            meilleure densité nutritionnelle pour la saison. »
            <div className="mt-3 font-sans text-[10px] uppercase not-italic tracking-[0.18em] text-ink-50">
              Suggestion · IA Batch
            </div>
          </div>
        </div>

        <div>
          <Eyebrow className="mb-4">Chapitre 03</Eyebrow>
          <h2 className="h-display mb-10 text-display-md">
            Une intelligence qui <span className="italic">cuisine</span> pour
            vous.
          </h2>
          <div className="grid gap-7">
            {items.map(([t, b], i) => (
              <div
                key={t}
                className={cn(
                  "grid grid-cols-[40px_1fr] gap-6 pb-6",
                  i < items.length - 1 && "border-b border-hair",
                )}
              >
                <div className="font-serif text-lg italic text-accent">
                  0{i + 1}
                </div>
                <div>
                  <h4 className="mb-2 font-serif text-[22px] font-medium tracking-[-0.005em]">
                    {t}
                  </h4>
                  <p className="text-[14.5px] leading-relaxed text-ink-70">
                    {b}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Chapter 04 — Pricing ─────────────────────────────────────────────
function ChapterPricing() {
  const plans = [
    {
      name: "L'Essentiel",
      price: "Gratuit",
      sub: "pour toujours",
      feats: [
        "Un menu hebdomadaire pré-composé",
        "Liste de courses standard",
        "Pas-à-pas batch cooking",
        "3 recettes substituables par mois",
      ],
      cta: "Commencer",
      accent: false,
    },
    {
      name: "Le Cahier",
      price: "7,90 €",
      sub: "/mois · ou 69 € /an",
      feats: [
        "Menus IA personnalisés selon vos contraintes",
        "Substitutions illimitées",
        "Liste optimisée par allée + budget",
        "Thèmes hebdomadaires & saisons",
        "Partage foyer (jusqu'à 4 membres)",
        "Mode hors-ligne",
      ],
      cta: "Essayer 14 jours",
      accent: true,
    },
  ];
  return (
    <section
      id="tarifs"
      className="bg-paper px-5 py-24 lg:px-[72px] lg:py-[120px]"
    >
      <div className="mb-14 text-center lg:mb-16">
        <Eyebrow className="mb-4 justify-center">Chapitre 04</Eyebrow>
        <h2 className="h-display text-display-lg">
          Deux <span className="italic">formules</span>, sans engagement.
        </h2>
      </div>

      <div className="mx-auto grid max-w-[1080px] gap-6 lg:grid-cols-2">
        {plans.map((p) => (
          <div
            key={p.name}
            className={cn(
              "relative px-8 py-10 lg:px-10 lg:py-12",
              p.accent
                ? "bg-foreground text-background"
                : "border border-hair bg-background text-foreground",
            )}
          >
            {p.accent && (
              <div className="absolute right-6 top-6 text-[10px] uppercase tracking-[0.2em] text-accent-dim">
                ★ Recommandé
              </div>
            )}
            <h3 className="mb-8 font-serif text-[28px] font-normal italic">
              {p.name}
            </h3>
            <div className="mb-9 flex items-baseline gap-2.5">
              <span className="font-serif text-[64px] font-normal leading-none tracking-[-0.02em]">
                {p.price}
              </span>
              <span className="text-[13px] opacity-60">{p.sub}</span>
            </div>
            <ul className="mb-10 list-none p-0">
              {p.feats.map((f) => (
                <li
                  key={f}
                  className={cn(
                    "flex items-start gap-3.5 border-b py-3 text-sm",
                    p.accent ? "border-white/10" : "border-hair",
                  )}
                >
                  <Check
                    className={cn(
                      "mt-1 size-3.5 shrink-0",
                      p.accent ? "text-accent-dim" : "text-accent",
                    )}
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              size="lg"
              variant={p.accent ? "cream" : "default"}
              className="w-full"
            >
              <Link to="/onboarding">{p.cta}</Link>
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="relative overflow-hidden px-5 py-32 lg:px-[72px] lg:py-[160px]">
      <HeroPhoto src={PHOTOS.herbs} blur={8} opacity={1} scale={1.04} />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, oklch(0.92 0.018 75 / 0.35) 0%, oklch(0.97 0.012 80 / 0.7) 100%)",
        }}
      />
      <div className="relative mx-auto max-w-[980px] text-center">
        <Eyebrow rule ruleRight accent className="mb-7 justify-center">
          Commencer aujourd'hui
        </Eyebrow>
        <h2 className="h-display mb-8 text-display-xl">
          Votre prochain dimanche,
          <br />
          <span className="italic text-accent">orchestré.</span>
        </h2>
        <p className="mx-auto mb-10 max-w-[620px] font-serif text-xl font-light italic leading-snug text-ink-70 lg:text-[22px]">
          Quatorze jours pour essayer. Aucune carte demandée.
        </p>
        <div className="inline-flex flex-wrap items-center justify-center gap-3.5">
          <Button asChild size="xl">
            <Link to="/onboarding">
              Essayer 14 jours <ArrowRight />
            </Link>
          </Button>
          <span className="text-[13px] text-ink-70">
            annulable à tout moment
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────
type FooterLink = { label: string; to?: string; href?: string };

const FOOTER_COLS: Array<[string, FooterLink[]]> = [
  [
    "Produit",
    [
      { label: "Le concept", href: "#concept" },
      { label: "Tarifs", href: "#tarifs" },
      { label: "Téléchargement" },
      { label: "Cahier des recettes", to: "/login" },
    ],
  ],
  [
    "Ressources",
    [
      { label: "FAQ", to: "/faq" },
      { label: "Journal" },
      { label: "Guide du marché" },
      { label: "Aide" },
    ],
  ],
  [
    "Société",
    [
      { label: "À propos", to: "/about" },
      { label: "Manifeste" },
      { label: "Presse" },
      { label: "Recrutement" },
    ],
  ],
  [
    "Légal",
    [
      { label: "Mentions légales", to: "/legal" },
      { label: "Confidentialité" },
      { label: "CGU" },
      { label: "Cookies" },
    ],
  ],
];

function SiteFooter() {
  return (
    <footer className="border-t border-hair bg-background px-5 pb-14 pt-16 lg:px-[72px] lg:pt-20">
      <div className="mb-12 grid gap-10 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr] lg:gap-12">
        <div>
          <Wordmark size="lg" className="mb-4" />
          <p className="max-w-[340px] font-serif text-[17px] font-light italic leading-relaxed text-ink-70">
            « Cuisiner moins, manger mieux. »
          </p>
        </div>
        {FOOTER_COLS.map(([title, items]) => (
          <div key={title}>
            <div className="mb-4 text-[11px] uppercase tracking-[0.22em] text-ink-50">
              {title}
            </div>
            <ul className="grid list-none gap-2.5 p-0">
              {items.map((item) => (
                <li key={item.label} className="text-[13.5px] text-ink-70">
                  {item.to ? (
                    <Link to={item.to} className="hover:text-foreground">
                      {item.label}
                    </Link>
                  ) : item.href ? (
                    <a href={item.href} className="hover:text-foreground">
                      {item.label}
                    </a>
                  ) : (
                    <span className="text-ink-40">{item.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Rule />
      <div className="flex flex-wrap items-center justify-between gap-3 pt-6 text-xs text-ink-50">
        <span>© 2026 Batch Cooking · Paris</span>
        <span className="font-serif italic">Imprimé avec soin sur le web.</span>
      </div>
    </footer>
  );
}
